import * as React from "react";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";

import { useAppDispatch, useAppSelector } from "app/hooks";
import AndroidQR from "assets/icons/ios/new-android-app-qr.svg";
import IPhoneQR from "assets/icons/ios/new-ios-app-qr.svg";
import { SunoLogo } from "components/icons";
import { SUNO_ANDROID_APP_URL, SUNO_IOS_APP_URL } from "constants/mobile-app";

import {
  clearState,
  selectAuthIsInitialLoad,
  selectAuthIsSuccess,
  selectCurrentUserToken,
  selectMfaPending
} from "../auth/auth-slice";
import LoginPromoPanel from "./login-promo/login-promo-panel.component";
import { SHORT_SCREEN } from "./login-promo/login-promo-sizing";

type LocationProps = {
  pathname: string;
  state: {
    from: Location;
  };
};

const APP_STORES = [
  {
    label: "iOS",
    href: SUNO_IOS_APP_URL,
    qr: IPhoneQR,
    alt: "App Store QR code"
  },
  {
    label: "Android",
    href: SUNO_ANDROID_APP_URL,
    qr: AndroidQR,
    alt: "Google Play QR code"
  }
];

export default function UnauthorizedLayout() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation() as unknown as LocationProps;
  const navigate = useNavigate();
  const isSuccess = useAppSelector(selectAuthIsSuccess);
  const isLogged = useAppSelector(selectCurrentUserToken);
  const mfaPending = useAppSelector(selectMfaPending);
  const isInitialLoad = useAppSelector(selectAuthIsInitialLoad);
  const white = theme.palette.common.white;
  useEffect(() => {
    return () => {
      if (isInitialLoad) {
        dispatch(clearState());
      }
    };
  }, [dispatch, isInitialLoad]);

  useEffect(() => {
    if (isSuccess && Boolean(isLogged)) {
      dispatch(clearState());
      if (location.state?.from) {
        navigate(location.state?.from, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [isSuccess, isLogged, dispatch, navigate, location.state]);

  // MFA challenge (ENG-1096): the login succeeded password-wise but a second
  // factor is required — route to the enroll/verify step. Centralized here so
  // both a fresh login and a refresh mid-challenge land on the right page.
  useEffect(() => {
    if (!mfaPending || isLogged) return;
    const target = mfaPending.enrollmentRequired
      ? "/mfa/enroll"
      : "/mfa/verify";
    if (location.pathname !== target) {
      navigate(target, { replace: true, state: location.state });
    }
  }, [mfaPending, isLogged, navigate, location.pathname, location.state]);

  return (
    <Grid container minHeight="100dvh" columns={{ xs: 6, md: 12 }}>
      <Grid
        item
        xs={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100dvh"
      >
        <Stack
          gap={5}
          m="auto"
          maxWidth={400}
          width="100%"
          alignItems="center"
          sx={{ form: { width: "100%" } }}
        >
          <Box sx={{ "& g": { fill: "black" } }}>
            <SunoLogo width={180} height={52} />
          </Box>

          <Outlet />
        </Stack>
      </Grid>
      <Grid
        display={{ xs: "none", md: "flex" }}
        item
        xs={6}
        bgcolor={theme.palette.sideMenu.main}
        minHeight="100dvh"
        flexDirection="column"
        justifyContent="center"
        position="relative"
        px={{ md: 5, lg: 8 }}
        py={6}
        boxSizing="border-box"
        sx={{ [SHORT_SCREEN]: { py: 4 } }}
      >
        {/* Cap and center the content so it reads as one block on wide
            monitors instead of stretching thinly across the whole panel;
            the sizes inside step up at xl so it doesn't look laptop-sized
            on a large screen. */}
        <Box width="100%" maxWidth={{ md: 660, xl: 860 }} mx="auto">
          <LoginPromoPanel>
            <Stack
              direction="row"
              alignItems="center"
              gap={3.5}
              px={3.25}
              py={2.75}
              sx={{
                [SHORT_SCREEN]: { py: 2 },
                borderRadius: "18px",
                bgcolor: alpha(white, 0.06),
                border: `1px solid ${alpha(white, 0.1)}`
              }}
            >
              <Stack gap={0.625} flexGrow={1}>
                <Typography variant="body3" fontWeight={700} color={white}>
                  Scan to install
                </Typography>
                <Typography variant="body1" color={alpha(white, 0.6)}>
                  Use the same account you use here.
                </Typography>
              </Stack>
              <Stack direction="row" gap={3.25}>
                {APP_STORES.map(({ label, href, qr, alt }) => (
                  <Stack key={label} gap={1.25} alignItems="center">
                    <Box
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      display="flex"
                    >
                      <Box
                        component="img"
                        src={qr}
                        alt={alt}
                        width={{ md: 96, xl: 116 }}
                        height={{ md: 96, xl: 116 }}
                      />
                    </Box>
                    <Link
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ color: white, fontWeight: 700 }}
                    >
                      {label}
                    </Link>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </LoginPromoPanel>
        </Box>

        <Box
          position="absolute"
          bottom={0}
          left="50%"
          maxWidth={720}
          width="100%"
          pb={2}
          sx={{ transform: "translateX(-50%)" }}
        >
          <Typography
            ml="auto"
            color={white}
            pr={8}
            display="block"
            width="fit-content"
          >
            Copyright © {new Date().getFullYear()} Suno Inc
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
